import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { FormField, email, form, maxLength, required, submit, validate } from '@angular/forms/signals';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { from } from 'rxjs';
import { finalize } from 'rxjs/operators';
import {
  BillingPaymentMethod,
  BillingPortalApi,
  BillingProfile,
  BillingProfileApi,
  ResponseError,
  UpdateBillingProfileRequest,
} from '@tourfold/libraries/tf-api-client-public-v2';
import { syncQueryParams } from '@tourfold/libraries/shared-util';
import {
  resolveSettingsLoadErrorDetails,
  SettingsInlineMessage,
  SettingsLoadError,
  type SettingsLoadErrorDetails,
} from '@tourfold/features/settings-ui';

interface BillingProfileFormData {
  billingFullName: string;
  billingBusinessName: string;
  billingEmail: string;
  billingAddressLine1: string;
  billingAddressLine2: string;
  billingCity: string;
  billingPostalCode: string;
  billingStateProvince: string;
}

// Stripe returns card brand values in lowercase / snake_case (`visa`, `mastercard`,
// `amex`, `unionpay`, `eftpos_au`, …). Map known brands to their canonical SaaS
// display spelling so the UI doesn't show "visa" or "Eftpos_au". Unknown values
// fall back to a simple capitalize so we still render something sensible.
const CARD_BRAND_LABELS: Record<string, string> = {
  amex: 'American Express',
  diners: 'Diners Club',
  discover: 'Discover',
  eftpos_au: 'EFTPOS Australia',
  jcb: 'JCB',
  link: 'Link',
  mastercard: 'Mastercard',
  unionpay: 'UnionPay',
  unknown: 'Card',
  visa: 'Visa',
};

function formatCardBrand(brand: string | null | undefined): string {
  const raw = (brand ?? '').trim();
  if (raw === '') return '';
  const key = raw.toLowerCase();
  return CARD_BRAND_LABELS[key] ?? key.charAt(0).toUpperCase() + key.slice(1);
}

const EMPTY_FORM: BillingProfileFormData = {
  billingFullName: '',
  billingBusinessName: '',
  billingEmail: '',
  billingAddressLine1: '',
  billingAddressLine2: '',
  billingCity: '',
  billingPostalCode: '',
  billingStateProvince: '',
};

@Component({
  selector: 'tf-billing-profile-tab',
  imports: [
    CommonModule,
    TranslateModule,
    FormField,
    InputTextModule,
    ButtonModule,
    TagModule,
    SettingsInlineMessage,
    SettingsLoadError,
  ],
  templateUrl: './billing-profile-tab.html',
  styleUrls: [
    './billing-profile-tab.scss',
    // The Enterprise notice + payment-method card reuse the parent BillingSettings
    // component's `.billing-section`, `.billing-link`, and `.billing-contact-card*`
    // styles. Angular's view encapsulation scopes those rules to the parent's host,
    // so pull both files in explicitly here to keep one source of truth instead of
    // duplicating the styling (and the layout/colors drifting apart).
    '../settings-billing.component.scss',
    '../settings-billing-current-plan.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BillingProfileTab {
  readonly profileSkeletonFields = [1, 2, 3, 4, 5, 6, 7, 8] as const;
  readonly active = input(false);
  /**
   * Whether to render the "Payment method" card under the editable profile form.
   * The parent passes `false` for Free tenants (no Stripe customer) and Enterprise
   * tenants (custom contract — Stripe Customer Portal does not apply).
   */
  readonly showPaymentMethod = input(true);
  /**
   * When true, hide the editable form and show a "contact CSM" card instead. Used
   * for Enterprise tenants whose billing details are managed by support, not by
   * the self-service form.
   */
  readonly enterpriseNotice = input(false);
  /**
   * CSM mailto target shown inside the Enterprise contact card. Bound from the
   * parent so the email string stays single-sourced with the Plan-tab variant.
   */
  readonly enterpriseContactEmail = input<string>('');
  /** Emitted when the "Contact CSM" button is clicked. Parent opens the mailto. */
  readonly contactSalesRequested = output<void>();

  private readonly billingProfileApi = inject(BillingProfileApi);
  private readonly billingPortalApi = inject(BillingPortalApi);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private profileLoadRequestId = 0;
  private paymentMethodLoadRequestId = 0;

  readonly profile = signal<BillingProfile | null>(null);
  readonly paymentMethod = signal<BillingPaymentMethod | null>(null);
  readonly isLoading = signal(true);
  readonly hasLoadedOnce = signal(false);
  readonly loadFailed = signal(false);
  readonly loadErrorDetails = signal<SettingsLoadErrorDetails | null>(null);
  readonly isSaving = signal(false);
  readonly saveFailed = signal(false);
  readonly isLoadingPaymentMethod = signal(false);
  readonly paymentMethodLoadFailed = signal(false);
  readonly paymentMethodActionFailed = signal(false);
  readonly billingCustomerMissing = signal(false);
  readonly isOpeningPortal = signal(false);

  readonly formModel = signal<BillingProfileFormData>({ ...EMPTY_FORM });

  readonly profileForm = form(this.formModel, (schema) => {
    validate(schema.billingFullName, ({ value, valueOf }) => {
      const hasFullName = value().trim().length > 0;
      const hasBusinessName = valueOf(schema.billingBusinessName).trim().length > 0;
      return hasFullName || hasBusinessName
        ? undefined
        : {
            kind: 'billing-identity-required',
            message: 'SETTINGS_BILLING.PROFILE.VALIDATION.FULL_NAME_REQUIRED',
          };
    });
    maxLength(schema.billingFullName, 255, {
      message: 'SETTINGS_BILLING.PROFILE.VALIDATION.TOO_LONG',
    });
    maxLength(schema.billingBusinessName, 255, {
      message: 'SETTINGS_BILLING.PROFILE.VALIDATION.TOO_LONG',
    });
    email(schema.billingEmail, {
      message: 'SETTINGS_BILLING.PROFILE.VALIDATION.EMAIL_INVALID',
    });
    required(schema.billingEmail, {
      message: 'SETTINGS_BILLING.PROFILE.VALIDATION.EMAIL_REQUIRED',
    });
    maxLength(schema.billingEmail, 320, {
      message: 'SETTINGS_BILLING.PROFILE.VALIDATION.TOO_LONG',
    });
    validate(schema.billingAddressLine1, ({ value }) => {
      return value().trim().length > 0
        ? undefined
        : {
            kind: 'billing-address-line1-required',
            message: 'SETTINGS_BILLING.PROFILE.VALIDATION.ADDRESS_LINE1_REQUIRED',
          };
    });
    maxLength(schema.billingAddressLine1, 255, {
      message: 'SETTINGS_BILLING.PROFILE.VALIDATION.TOO_LONG',
    });
    maxLength(schema.billingAddressLine2, 255, {
      message: 'SETTINGS_BILLING.PROFILE.VALIDATION.TOO_LONG',
    });
    validate(schema.billingCity, ({ value }) => {
      return value().trim().length > 0
        ? undefined
        : {
            kind: 'billing-city-required',
            message: 'SETTINGS_BILLING.PROFILE.VALIDATION.CITY_REQUIRED',
          };
    });
    maxLength(schema.billingCity, 120, {
      message: 'SETTINGS_BILLING.PROFILE.VALIDATION.TOO_LONG',
    });
    validate(schema.billingPostalCode, ({ value }) => {
      return value().trim().length > 0
        ? undefined
        : {
            kind: 'billing-postal-code-required',
            message: 'SETTINGS_BILLING.PROFILE.VALIDATION.POSTAL_CODE_REQUIRED',
          };
    });
    maxLength(schema.billingPostalCode, 20, {
      message: 'SETTINGS_BILLING.PROFILE.VALIDATION.TOO_LONG',
    });
    maxLength(schema.billingStateProvince, 120, {
      message: 'SETTINGS_BILLING.PROFILE.VALIDATION.TOO_LONG',
    });
  });

  readonly readonlyCountry = computed(() => this.profile()?.billingCountryCode ?? '');
  readonly readonlyVatNumber = computed(() => this.profile()?.billingVatNumber ?? '');
  readonly showInitialLoading = computed(
    () => this.isLoading() && !this.hasLoadedOnce() && !this.loadFailed()
  );
  readonly showInitialError = computed(() => this.loadFailed() && !this.hasLoadedOnce());
  readonly showReloadError = computed(() => this.loadFailed() && this.hasLoadedOnce());

  readonly paymentMethodSummary = computed(() => {
    const pm = this.paymentMethod();
    if (!pm?.hasPaymentMethod) return null;
    return {
      brand: formatCardBrand(pm.brand),
      last4: pm.last4 ?? '',
      expMonth: pm.expMonth ?? 0,
      expYear: pm.expYear ?? 0,
    };
  });

  constructor() {
    effect(() => {
      if (
        this.active() &&
        !this.enterpriseNotice() &&
        this.profile() === null &&
        !this.isLoading() &&
        !this.loadFailed()
      ) {
        this.load();
      }
    });
    // The payment-method card is gated on `showPaymentMethod`, which is derived
    // from the parent's `currentPlan().planKind`. The parent's overview fetch and
    // this child's profile fetch run in parallel, so `showPaymentMethod` can
    // still be false when `load()` resolves and the inline gate inside `load()`
    // would skip the PM fetch forever. Drive it from its own effect so we react
    // when the gate flips to true after the profile is already loaded.
    effect(() => {
      if (
        this.active() &&
        this.showPaymentMethod() &&
        this.profile() !== null &&
        this.paymentMethod() === null &&
        !this.isLoadingPaymentMethod() &&
        !this.paymentMethodLoadFailed() &&
        !this.billingCustomerMissing()
      ) {
        this.loadPaymentMethod();
      }
    });
    this.load();
    this.handlePaymentMethodReturn();
  }

  refresh(): void {
    this.load();
  }

  onSave(): void {
    submit(this.profileForm, async () => {
      const data = this.formModel();
      const request: UpdateBillingProfileRequest = {
        billingFullName: data.billingFullName.trim() || undefined,
        billingBusinessName: data.billingBusinessName.trim() || undefined,
        billingEmail: data.billingEmail.trim(),
        billingAddressLine1: data.billingAddressLine1.trim() || undefined,
        billingAddressLine2: data.billingAddressLine2.trim() || undefined,
        billingCity: data.billingCity.trim() || undefined,
        billingPostalCode: data.billingPostalCode.trim() || undefined,
        billingStateProvince: data.billingStateProvince.trim() || undefined,
        // Read-only fields — forward existing values unchanged.
        billingCountryCode: this.profile()?.billingCountryCode ?? undefined,
        billingVatNumber: this.profile()?.billingVatNumber ?? undefined,
      };

      this.isSaving.set(true);
      this.saveFailed.set(false);
      this.paymentMethodActionFailed.set(false);

      return new Promise<void>((resolve) => {
        from(this.billingProfileApi.putBillingProfile({ updateBillingProfileRequest: request }))
          .pipe(
            finalize(() => this.isSaving.set(false)),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe({
            next: (updated) => {
              this.profile.set(updated);
              this.applyProfileToForm(updated);
              resolve();
            },
            error: () => {
              this.saveFailed.set(true);
              resolve();
            },
          });
      });
    });
  }

  openPaymentPortal(): void {
    if (this.isOpeningPortal()) return;
    this.isOpeningPortal.set(true);
    this.paymentMethodActionFailed.set(false);
    this.billingCustomerMissing.set(false);

    const origin = window.location.origin;
    const pathname = window.location.pathname;
    const returnUrl = `${origin}${pathname}?tab=profile&paymentMethodUpdated=1`;

    from(
      this.billingPortalApi.createBillingPortalSession({
        billingPortalSessionRequest: { returnUrl },
      }),
    )
      .pipe(
        finalize(() => this.isOpeningPortal.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (session) => {
          window.location.href = session.url;
        },
        error: (err: unknown) => {
          if (err instanceof ResponseError && err.response.status === 422) {
            // Tenant raced past the Free-plan gate (e.g. plan just changed) —
            // surface the friendly "subscribe to manage" copy.
            this.billingCustomerMissing.set(true);
            return;
          }
          this.paymentMethodActionFailed.set(true);
        },
      });
  }

  private load(): void {
    // Enterprise tenants only see a "contact support" notice — no API call needed.
    if (this.enterpriseNotice()) {
      this.profileLoadRequestId += 1;
      this.isLoading.set(false);
      return;
    }

    const requestId = ++this.profileLoadRequestId;
    this.isLoading.set(true);
    this.loadFailed.set(false);
    this.loadErrorDetails.set(null);
    this.paymentMethodLoadFailed.set(false);
    this.paymentMethodActionFailed.set(false);
    this.billingCustomerMissing.set(false);

    from(this.billingProfileApi.getBillingProfile())
      .pipe(
        finalize(() => {
          if (this.profileLoadRequestId === requestId) {
            this.isLoading.set(false);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (profile) => {
          if (this.profileLoadRequestId !== requestId || this.enterpriseNotice()) {
            return;
          }
          this.profile.set(profile);
          this.applyProfileToForm(profile);
          this.hasLoadedOnce.set(true);
          this.loadFailed.set(false);
          this.loadErrorDetails.set(null);
          // Payment-method fetch is driven by the effect in the constructor — it
          // fires once `profile` is set AND `showPaymentMethod` is true, which
          // handles the race where the parent's overview fetch (the source of
          // the gate signal) resolves after this profile fetch.
        },
        error: (error: unknown) => {
          if (this.profileLoadRequestId !== requestId || this.enterpriseNotice()) {
            return;
          }
          this.loadFailed.set(true);
          void this.updateLoadErrorDetails(error, requestId);
        },
      });
  }

  private loadPaymentMethod(): void {
    const requestId = ++this.paymentMethodLoadRequestId;
    this.isLoadingPaymentMethod.set(true);
    this.paymentMethodLoadFailed.set(false);
    this.paymentMethodActionFailed.set(false);
    this.billingCustomerMissing.set(false);

    from(this.billingPortalApi.getBillingPaymentMethod())
      .pipe(
        finalize(() => {
          if (this.paymentMethodLoadRequestId === requestId) {
            this.isLoadingPaymentMethod.set(false);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (pm) => {
          if (this.paymentMethodLoadRequestId !== requestId || !this.showPaymentMethod()) {
            return;
          }
          this.paymentMethod.set(pm);
          this.paymentMethodLoadFailed.set(false);
        },
        error: (err: unknown) => {
          if (this.paymentMethodLoadRequestId !== requestId || !this.showPaymentMethod()) {
            return;
          }
          // The typescript-fetch runtime wraps non-2xx responses in ResponseError
          // with the raw fetch Response attached. The only documented 422 from
          // `GET /api/v2/billing/payment-method` is `billing-customer-missing`, so
          // we treat any 422 here as the "no Stripe customer yet" case and render
          // the friendly "subscribe to manage" copy instead of a generic error.
          if (err instanceof ResponseError && err.response.status === 422) {
            this.billingCustomerMissing.set(true);
            return;
          }
          this.paymentMethodLoadFailed.set(true);
        },
      });
  }

  private applyProfileToForm(profile: BillingProfile): void {
    this.formModel.set({
      billingFullName: profile.billingFullName ?? '',
      billingBusinessName: profile.billingBusinessName ?? '',
      billingEmail: profile.billingEmail ?? '',
      billingAddressLine1: profile.billingAddressLine1 ?? '',
      billingAddressLine2: profile.billingAddressLine2 ?? '',
      billingCity: profile.billingCity ?? '',
      billingPostalCode: profile.billingPostalCode ?? '',
      billingStateProvince: profile.billingStateProvince ?? '',
    });
  }

  private handlePaymentMethodReturn(): void {
    const flag = this.route.snapshot.queryParamMap.get('paymentMethodUpdated');
    if (flag !== '1') return;
    this.paymentMethod.set(null);
    this.paymentMethodLoadFailed.set(false);
    this.paymentMethodActionFailed.set(false);
    this.billingCustomerMissing.set(false);
    syncQueryParams(this.router, this.route, { paymentMethodUpdated: undefined });
  }

  clearSaveError(): void {
    this.saveFailed.set(false);
  }

  clearLoadError(): void {
    this.loadFailed.set(false);
    this.loadErrorDetails.set(null);
  }

  clearPaymentMethodLoadError(): void {
    this.paymentMethodLoadFailed.set(false);
  }

  clearPaymentMethodActionError(): void {
    this.paymentMethodActionFailed.set(false);
  }

  private async updateLoadErrorDetails(error: unknown, requestId: number): Promise<void> {
    const details = await resolveSettingsLoadErrorDetails(error);
    if (this.profileLoadRequestId === requestId && this.loadFailed()) {
      this.loadErrorDetails.set(details);
    }
  }
}
